import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as _ from 'lodash-es';
import { first } from 'rxjs/operators';
import { NotificationType } from '~core/enum';
import { ButtonsTranslations } from '~shared/components/buttons/buttons-translations.service';
import { SpinnerService } from '~shared/components/buttons/spinner/spinner.service';
import { FormLabelTranslationService } from '~shared/components/form-fields/translations/form-label-translations.service';
import { noWhitespaceCharacters } from '~shared/directives/form-validators';
import { AuthenticatedUser, Club, Customer, Owner, Reminder } from '~core/models';
import { DefaultNotificationService, ReminderService, StorageService } from '~core/services';
import { ReminderStatusTranslationsService } from '~shared/translations';

/**
 * Handles reminders.
 */
@UntilDestroy()
@Component({
  selector: 'app-home-reminder-edit',
  templateUrl: 'home-reminder-edit.component.html'
})
export class HomeReminderEditComponent implements OnInit, OnDestroy {
  public updateReminderForm: UntypedFormGroup;
  public currentReminder: Reminder;
  public spinnerName: string = 'reminderUpdate';
  public dateToday: Date = new Date();
  public file: any;
  public selectedClub: Club;
  public possibleOwnersList: Array<Owner> = [];
  public pageTitle: string;
  public pageLoading: boolean;

  public attachmentLoading: boolean;
  public spinnerAttachmentLoadName: string = 'attachmentLoading';
  public spinnerLoading: boolean;
  public spinnerLoadName: string = 'editReminderLoading';

  @Input() public customer: Customer;
  @Input() public authenticatedUser: AuthenticatedUser;
  @Input() public sidebarOpened: boolean;
  @Output() public sidebarOpenedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(
    private notificationService: DefaultNotificationService,
    private reminderService: ReminderService,
    private formBuilder: UntypedFormBuilder,
    private spinnerService: SpinnerService,
    private storageService: StorageService,
    public reminderTranslations: ReminderStatusTranslationsService,
    public buttonsTranslations: ButtonsTranslations,
    public flTranslations: FormLabelTranslationService,
  ) {
    this.storageService.castSelectedClub.pipe(untilDestroyed(this))
      .subscribe((data: Club) => this.selectedClub = data);
    this.reminderService.castCurrentPossibleOwnerList.pipe(untilDestroyed(this))
      .subscribe((data: Array<Owner>) => this.possibleOwnersList = data);
  }

  public ngOnDestroy(): void { }

  public ngOnInit(): any {
    this.pageTitle = $localize`:@@customerOverviewComponent.modalTitle.editReminder:Edit Reminder`;

    this.reminderService.castCurrentReminder.pipe(untilDestroyed(this))
      .subscribe((data: Reminder) => {
        if (data) {
          this.currentReminder = data;
          this.loadPossibleOwners();
          this.showAttachment();
          this.fillReminderForm();
        }
      });
  }

  /**
   * Create the contact form.
   */
  private fillReminderForm(): void {
    this.updateReminderForm = this.formBuilder.group({
      id: [this.currentReminder.id],
      text: [this.currentReminder.text, [
        Validators.required,
        Validators.maxLength(1000),
        noWhitespaceCharacters
      ]],
      deadline: [this.currentReminder.deadline?.dateIso, [
        Validators.required
      ]],
      status: [this.currentReminder.status.value, [
        Validators.required
      ]],
      owner: [this.currentReminder.owner.id, [
        Validators.required
      ]],
      customer: [`api/customers/${this.customer.id}`]
    });
  }

  /**
   * Load all the possible owners that the user can assign
   */
  private loadPossibleOwners(): void {
    if (!this.possibleOwnersList) {
      this.pageLoading = true;
      this.reminderService.getPossibleReminderOwners(this.selectedClub.id).pipe(first())
        .subscribe((data: Array<Owner>) => {
          this.possibleOwnersList = data;
          this.spinnerService.hide(this.spinnerLoadName);
        });
    }
  }

  /**
   * Update the reminder that you clicked on.
   */
  public updateReminder(): void {
    if (this.updateReminderForm.valid) {
      this.spinnerLoading = true;
      this.updateReminderForm.controls['owner'].setValue(`api/users/${this.updateReminderForm.controls['owner'].value}`);

      this.reminderService.update(this.updateReminderForm.value).pipe(untilDestroyed(this))
        .subscribe({
          next: (reminder: Reminder) => {
            this.spinnerService.hide(this.spinnerName);
            this.reminderService.updateReminder(reminder);

            if (reminder.owner.id !== this.authenticatedUser.id) {
              this.reminderService.deleteReminderFromList(reminder.id);
            }

            this.notificationService.showNotification({
              type: NotificationType.SUCCESS,
              message: this.reminderTranslations.i18nSuccessReminderUpdate
            });
            this.sidebarOpenedChange.emit(false);
          },
          error: (error: Error) => {
            this.spinnerService.hide(this.spinnerName);
            this.notificationService.showNotification({
              type: NotificationType.ALERT,
              message: this.reminderTranslations.i18nErrorReminderUpdate
            });
          }
        });
    }
  }

  /**
   * Retrieves the attachment file and shows the name to the user
   */
  public showAttachment(): void {
    if (this.currentReminder.attachment) {
      this.attachmentLoading = true;
      this.reminderService.getPathToImageByMediaObject(this.currentReminder.attachment).pipe(first())
        .subscribe({
          next: (res: any) => {
            this.file = {
              contentUrl: res.contentUrl,
              originalName: res.originalName,
            };
            this.attachmentLoading = false;
          },
          error: (error: Error) => {
            this.notificationService.showNotification({
              type: NotificationType.ALERT,
              message: this.reminderTranslations.i18nErrorCannotDownloadAttachment
            });
            this.attachmentLoading = false;
          }
        });
    }
  }

  /**
   * Downloads the attachment file
   */
  public downloadAttachment(): void {
    if (this.currentReminder.attachment) {
      const link = document.createElement('a');
      link.setAttribute('type', 'hidden');
      link.setAttribute('download', `${this.file.originalName}`);
      link.setAttribute('href', `${this.file.contentUrl}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  }

  get availableStatuses(): any {
    const statuses = this.currentReminder.statuses;
    _.remove(statuses, el => el.value === 'overdue');
    return statuses;
  }

  get preselectedValue(): any {
    if (this.currentReminder.status.value === 'closed') {
      return 'closed';
    } else {
      return 'open';
    }
  }
}
